<template>
  <v-menu
    v-model="isOpen"
   :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    min-width="290px"
  >
    <template #activator="{ props: menuProps }">
      <v-text-field
        v-bind="menuProps"
        v-model="displayValue"
        :label="label"
        :readonly="true"
        :clearable="true"
        :rules="rules"
        prepend-inner-icon="mdi-calendar"
        density="compact"
        @click:clear="clearDate"
        variant="solo-filled"
      />
    </template>

    <v-date-picker
    show-adjacent-months
      v-model="internalValue"
      color="primary"
      :multiple="props.range ? 'range':false"
      @update:modelValue="onDateSelected"
    />
  </v-menu>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { format, parseISO, isValid } from 'date-fns';

const props = defineProps<{
  modelValue: string | [string, string] | null;
  label?: string;
  rules?: ((v: any) => boolean | string)[];
  range?: boolean;
}>();

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);
const internalValue = ref<Date | Date[] | null>(null);

// Conversión inicial de string a Date(s)
watch(
  () => props.modelValue,
  val => {
    if (!val) {
      internalValue.value = null;
    } else if (Array.isArray(val)) {
      internalValue.value = val.map(d => parseISO(d)).filter(isValid);
    } else {
      const parsed = parseISO(val);
      internalValue.value = isValid(parsed) ? parsed : null;
    }
  },
  { immediate: true }
);


// Conversión de Date(s) a string(s)
const onDateSelected = (val: Date | Date[]) => {
  if (Array.isArray(val)) {
    const sorted = val.filter(Boolean).sort((a, b) => a.getTime() - b.getTime());
    internalValue.value = sorted;

    // Si ya hay dos fechas distintas, emitimos y cerramos
    if (sorted.length >= 2) {
      const formatted = [sorted[0].getTime(), sorted[sorted.length - 1].getTime()].map(d => format(d, 'yyyy-MM-dd')) as [string, string];
      emit('update:modelValue', formatted);
      isOpen.value = false;
    }
  } else {
    internalValue.value = val;
    emit('update:modelValue', format(val, 'yyyy-MM-dd'));
    isOpen.value = false;
  }
};


// Mostrar valor en el text-field
const displayValue = computed(() => {
  if (!internalValue.value) return '';

  const formatSafe = (d: unknown) => {
    const parsed = d instanceof Date ? d : parseISO(String(d));
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : '';
  };

  if (Array.isArray(internalValue.value)) {
    const [start, end] = internalValue.value;
    return `${formatSafe(start)} - ${formatSafe(end)}`;
  }

  return formatSafe(internalValue.value);
});

const clearDate = () => {
  internalValue.value = null;
  emit('update:modelValue', null);
};
</script>
